import { iEmsApiResponseBody } from "../Logic/TenantAPI";
import { MailboxTestResultBase, MailboxTestStage } from "./MailboxTestAPI";

/**
 * What one test call means to the operator, independent of how it is drawn.
 *
 * The split that matters is `passed` / `failed` / `busy` versus everything
 * else. Those three are a `200`: the test RAN, and its verdict is about the
 * mailbox. Every other kind is about the request — who sent it, whether the
 * mailbox exists, whether the network was there — and says nothing about the
 * mailbox's settings at all.
 *
 * `busy` is its own kind, not a `failed` with a softer label. Contract §3.5:
 * a `Lock` result means the processor is running this mailbox right now. The
 * settings may be perfect. Drawing it with the failure treatment would send
 * the admin hunting for a fault that does not exist.
 */
export type TestOutcome<T extends MailboxTestResultBase> =
    | { kind: "passed"; result: T }
    | { kind: "failed"; label: string; result: T }
    | { kind: "busy"; result: T }
    | { kind: "invalid"; messages: string[] }
    | { kind: "forbidden" }
    | { kind: "notFound" }
    | { kind: "unauthorised" }
    | { kind: "network" }
    | { kind: "error"; status: number };

/**
 * The brief's label for each failing stage.
 *
 * `Completed` never reaches this table — a completed test is `passed` — and
 * `Lock` is `busy`, worded separately. Both stay in the `Record` anyway so a
 * new stage fails to compile here rather than rendering a blank label.
 */
const STAGE_LABEL: Record<MailboxTestStage, string> = {
    Resolve: "Settings are incomplete or invalid",
    Lock: "The mailbox is being processed now. Try again shortly.",
    Connect: "Could not connect or sign in to the mail server",
    Send: "The mail server rejected the test email",
    Completed: "The test completed",
};

export function stageLabel(stage: MailboxTestStage): string {
    return STAGE_LABEL[stage] ?? `The test stopped at ${stage}`;
}

export function testOutcome<T extends MailboxTestResultBase>(response: iEmsApiResponseBody<T>): TestOutcome<T> {
    if (response.status === 200) {
        const result = response.data;
        if (!result) {
            return { kind: "error", status: 200 };
        }
        // `success` decides, not the stage. The stage says where a FAILED test
        // stopped; reading it first would let a mismatched pair render a pass
        // as a failure.
        if (result.success) {
            return { kind: "passed", result };
        }
        if (result.stage === "Lock") {
            return { kind: "busy", result };
        }
        return { kind: "failed", label: stageLabel(result.stage), result };
    }

    switch (response.status) {
        case 0:
            return { kind: "network" };
        case 400:
            return { kind: "invalid", messages: recipientErrors(response.validationErrors) };
        case 401:
            return { kind: "unauthorised" };
        case 403:
            return { kind: "forbidden" };
        case 404:
            return { kind: "notFound" };
        default:
            return { kind: "error", status: response.status };
    }
}

/**
 * The messages to show next to the recipient field, from a `400` body.
 *
 * Takes `unknown`: `CallEmsApi` stores the whole `ValidationProblemDetails`
 * in a field typed as its inner `errors` map (see TenantEmails/searchErrors.ts
 * for the full story), so the declared type cannot be trusted here.
 *
 * The send route has one input, so every message is about the recipient
 * whatever key it arrives under — `Recipient` from FluentValidation, or a
 * `$.recipient` JSON path if the body failed to bind. The recipient key is
 * preferred when present; otherwise every message is shown, because hiding
 * one would leave the admin with a refusal and no reason.
 */
export function recipientErrors(body: unknown): string[] {
    if (!body || typeof body !== "object") {
        return [];
    }

    const problem = body as { title?: unknown; errors?: Record<string, unknown> };
    const errors = problem.errors && typeof problem.errors === "object" ? problem.errors : {};

    const strings = (value: unknown): string[] =>
        Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];

    const recipientKey = Object.keys(errors).find(key => key.toLowerCase() === "recipient");
    const messages = recipientKey ? strings(errors[recipientKey]) : Object.values(errors).flatMap(strings);

    if (messages.length === 0 && typeof problem.title === "string") {
        return [problem.title];
    }
    return messages;
}

/**
 * A client-side sanity check, not validation. The server decides (it returns
 * `400` for anything it rejects); this only stops an obviously incomplete
 * address from costing a round trip. Deliberately loose: something@domain.tld
 * with no spaces.
 */
export function isPlausibleEmail(address: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address.trim());
}
